<template>
  <v-row>
    <v-col sm="6">
      <v-select
        :dense="dense"
        v-model="computedValue.name"
        :items="availableMarkets"
        label="Market"
        :rules="[(v) => !!v || 'required']"
        @change="$emit('change')"
      ></v-select>
    </v-col>
    <v-col sm="6">
      <v-select
        :disabled="!computedValue.name"
        :dense="dense"
        v-model="computedValue.index"
        :items="allowedIndexes"
        label="Index Type"
        item-text="text"
        :rules="[(v) => !!v || 'required']"
        @change="$emit('change')"
      ></v-select>
    </v-col>
    <v-col sm="12">
      <InputMarketNode
        v-if="showNodeData"
        :dense="dense"
        v-model="computedMarketNode"
        :market="value.name"
        :index-type="value.index"
        :key="`node-dd-${nodeDropdownKey}`"
        @change="$emit('change')"
      ></InputMarketNode>
    </v-col>
  </v-row>
</template>

<script>
import { mapActions } from "vuex";
import marketSettings from "@/mixins/marketsSettings";
import { makeFindMixin } from "feathers-vuex";

export default {
  name: "InputPriceMarket",
  components: {
    InputMarketNode: () => import("@/components/InputMarketNode.vue"),
  },
  mixins: [
    makeFindMixin({
      service() {
        return "data-services/market-inventory";
      },
      name: "market-inventory",
      watch: true,
    }),
  ],
  props: {
    allowedSchedules: {
      type: Array,
      required: false,
      default: () => [
        { text: "Real-Time", value: "RT" },
        { text: "Day-Ahead", value: "DA" },
      ],
    },
    energyPriceSource: {
      type: String,
      required: false,
      default: "market",
    },
    ancillaryPriceSource: {
      type: String,
      required: false,
      default: "market",
    },
    autoSelectFirstMarket: {
      type: Boolean,
      required: false,
      default: false,
    },
    dense: {
      type: Boolean,
      required: false,
      default: false,
    },
    num_years: {
      type: Number,
      default: 2,
    },
    dateRange: {
      type: Boolean,
      required: false,
      default: false,
    },
    value: {
      type: Object,
      required: false,
      default() {
        return {
          name: null,
          index: null,
          price_type: null,
          year_start: null,
          year_end: null,
          years: [],
          list: [],
          node: null,
          zone: null,
        };
      },
    },
  },
  computed: {
    marketInventoryFetchParams() {
      return {
        query: {
          $limit: 1000,
          $skip: 0,
        },
        debounce: 500,
      };
    },
    marketIndex() {
      return this.value ? this.value.index : null;
    },
    marketInventoryParams() {
      return {
        query: {
          "Data Provider": this.currentMarket,
          $select: ["Data Provider", "Table Name", "Years", "Market"],
        },
      };
    },
    endYearRange() {
      return this.value.year_start
        ? this.yearRange.filter((year) => year >= this.value.year_start)
        : this.yearRange;
    },
    showNodeData() {
      return (
        this.value &&
        ["CAISO", "CHILE", "ERCOT", "ISONE", "MISO", "NYISO", "PJM", "SPP"].includes(
          this.value.name
        )
      );
    },
    currentMarket() {
      return this.value ? this.value.name : null;
    },
    currentMarketSettings() {
      if (!this.currentMarket) return {};
      if (this.currentMarket === "ERCOT") {
        const indexType = this.value.index === "DA" ? "Day-Ahead" : "Real-Time";
        return marketSettings[this.currentMarket][indexType];
      }
      return this.currentMarket ? marketSettings[this.currentMarket] : {};
    },
    computedMarketNode: {
      get() {
        return this.marketNode;
      },
      set(val) {
        this.marketNode = val;
        const valueCopy = this.value;
        valueCopy.node = val[this.currentMarketSettings.nodeKey] || null;
        this.$emit("input", valueCopy);
        this.$emit("change");
      },
    },
    currentUser() {
      return this.$auth.user;
    },
    computedValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
        this.$emit("change");
      },
    },
  },
  data() {
    return {
      yearRange: [],
      yearText: false,
      nodeDropdownKey: 1,
      marketNode: null,
      allowedIndexes: [],
      availableMarkets: [],
    };
  },
  created() {},
  mounted() {
    this.getUser(this.currentUser.id).then((user) => {
      this.availableMarkets = user.dataLicenses.map((l) => l.name);
      if (this.autoSelectFirstMarket && this.availableMarkets.length && !this.computedValue.name) {
        [this.computedValue.name] = this.availableMarkets;
      }
    });
  },
  watch: {
    energyPriceSource() {
      if (this.marketInventory.length) {
        this.yearRangeFunction(this.value.index);
      }
    },
    ancillaryPriceSource() {
      if (this.marketInventory.length) {
        this.yearRangeFunction(this.value.index);
      }
    },
    marketIndex(val) {
      if (val) {
        if (this.marketInventory.length) {
          this.yearText = false;
          this.yearRangeFunction(val);
        } else {
          this.yearText = true;
        }
      }
    },
    currentMarket(v) {
      if (v) {
        if (this.marketInventory.length) {
          this.yearText = false;
          this.allowedIndexes = this.marketInventory.map((el) => ({
            text: el.Market,
            value: el.Market === "Day-Ahead" ? "DA" : "RT",
          }));
        } else {
          this.allowedIndexes = [
            {
              text: "Day-Ahead",
              value: "DA",
            },
            {
              text: "Real-Time",
              value: "RT",
            },
          ];
        }
      }
      this.nodeDropdownKey += 1;
      const valueCopy = this.value;
      valueCopy.node = null;
      valueCopy.index = null;
      if (this.autoSelectFirstMarket && !this.computedValue.index) {
        valueCopy.index = this.allowedIndexes[0].value;
      }
      this.$emit("input", valueCopy);
      this.$emit("change");
    },
  },
  methods: {
    yearRangeFunction(index) {
      const indexText = index === "DA" ? "Day-Ahead" : "Real-Time";
      if (this.energyPriceSource === "market" && this.ancillaryPriceSource === "market") {
        const years = this.marketInventory
          .filter((el) => el.Market === indexText)
          .map((el) => el.Years.sort());
        const startYear = Math.max(...years.map((el) => el[0]));
        const endYear = Math.min(...years.map((el) => el[el.length - 1]));
        this.yearRange = Array.from({ length: endYear - startYear + 1 }, (_, i) => startYear + i);
      } else if (this.ancillaryPriceSource === "market" && this.energyPriceSource === "custom") {
        this.yearRange = this.marketInventory
          .find((el) => el.Market === indexText && el["Table Name"].includes("ancillary"))
          .Years.sort();
        if (!this.yearRange.length) {
          this.yearText = true;
        }
      } else {
        this.yearRange = this.marketInventory
          .find((el) => el.Market === indexText && !el["Table Name"].includes("ancillary"))
          .Years.sort();
        if (!this.yearRange.length) {
          this.yearText = true;
        }
      }
      if (this.autoSelectFirstMarket) {
        [this.computedValue.year_start] = this.yearRange;
        this.computedValue.year_end = this.yearRange[this.yearRange.length - 1];
      }
    },
    ...mapActions({
      getUser: "users/getAcelerexUser",
    }),
    change() {
      this.$emit("change");
      this.$emit("input", this.value);
    },
  },
};
</script>
